import React, { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * PasswordInput — a text input that toggles between type="password" and
 * type="text" via a right-aligned eye icon. Behaves like a plain <input>:
 * accepts any prop the caller wants (value/onChange/required/data-testid…)
 * and forwards a ref for imperative focus.
 *
 * `data-testid={id}` still targets the underlying input. A companion toggle
 * button is exposed as `{id}-toggle` for click-to-reveal tests.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testId, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  // Icon convention: line-through = covered/hidden, no line = revealed/visible.
  const Icon = visible ? Eye : EyeOff;
  // Split any margin classes off the passed-in className and hoist them to
  // the wrapping <div> so `top-1/2` centers within the input frame ONLY
  // (not within input + margin). Everything else — width, extra padding,
  // borders — stays on the input where the caller expected it.
  const marginRe = /(^|\s)m[trblxy]?-\S+/g;
  const wrapperMargins = (className.match(marginRe) || []).join(" ").trim();
  const inputClassName = className.replace(marginRe, " ").replace(/\s+/g, " ").trim();
  return (
    <div className={`relative ${wrapperMargins}`}>
      <input
        ref={ref}
        type={visible ? "text" : "password"}
        data-testid={testId}
        className={`sc-input !pr-10 ${inputClassName}`}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        data-testid={testId ? `${testId}-toggle` : undefined}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 pr-2 pl-2 flex items-center text-zinc-400 hover:text-[color:var(--sc-cyan)] transition"
        tabIndex={-1}
      >
        <Icon className="w-4 h-4" />
      </button>
    </div>
  );
});

export default PasswordInput;
